import { useEffect, useState, useCallback, FocusEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import { sessionEnd } from "../../redux/actions";
import axios from "axios";
import { urlAPI } from "../../config/config";
import {
  Box, CssBaseline, Card, Grid, TextField, Autocomplete, MenuItem, Button, Typography,
} from "@mui/material";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import NavTab from "../../layout/NavTab";

import { ISalary } from "../../interface/salary";
import { IRegieSimulationInputs, IRegieSimulationResults, IRegieBaremes, RegieVehicleType } from "../../interface/regie";
import { getRegieBaremes } from "../../tools/regie/getRegieBaremes";
import { simulateRegie } from "../../tools/regie/simulateRegie";
import { saveRegieSimulation } from "../../tools/regie/saveRegieSimulation";
import RegieResultsPanel from "../../components/Regie/RegieResultsPanel";
import RegieHistoryDialog from "../../components/Regie/RegieHistoryDialog";

const selectAllOnFocus = (e: FocusEvent<HTMLInputElement>) => e.target.select();

const emptyInputs: IRegieSimulationInputs = {
  brutMensuelPropose: 0,
  joursConges: 25,
  joursRTT: 0,
  joursFormation: 0,
  joursNonFactures: 0,
  absences: 0,
  kmPrevisionnelAn: 0,
  vehiculeType: null,
  puissanceFiscale: null,
  abonnementTransport: 0,
  fraisAnnuelsDivers: 0,
  margeCible: 0.40,
  tjmPropose: 0,
};

export default function RegieSimulationForm() {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);

  const [salaries, setSalaries] = useState<ISalary[]>([]);
  const [selectedSalary, setSelectedSalary] = useState<ISalary | null>(null);
  const [baremes, setBaremes] = useState<IRegieBaremes | null>(null);
  const [inputs, setInputs] = useState<IRegieSimulationInputs>(emptyInputs);
  const [results, setResults] = useState<IRegieSimulationResults | null>(null);
  const [error, setError] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchSalaries = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${urlAPI}/salary`, { headers: { Authorization: `Bearer ${token}` } });
      setSalaries(response.data.data);
    } catch (err) {
      setError("Erreur lors de la récupération des salariés");
    }
  }, [token]);

  useEffect(() => { fetchSalaries(); }, [fetchSalaries]);

  useEffect(() => {
    if (!token) return;
    getRegieBaremes(token, (b) => {
      setBaremes(b);
      setInputs((previous) => ({ ...previous, margeCible: b.scalars.margeCibleDefaut }));
    }, dispatch, setError);
  }, [token, dispatch]);

  useEffect(() => {
    if (!token || inputs.brutMensuelPropose <= 0 || inputs.tjmPropose <= 0) {
      setResults(null);
      return;
    }
    const timeout = setTimeout(() => {
      simulateRegie(inputs, token, setResults, dispatch, setError);
    }, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, inputs]);

  const puissancesFiscalesDisponibles = (baremes?.ik || [])
    .filter((bracket) => bracket.vehicleType === inputs.vehiculeType)
    .filter((bracket, index, all) => all.findIndex((other) => other.fiscalPowerBracket === bracket.fiscalPowerBracket) === index);

  const handleSave = () => {
    if (!token || !selectedSalary) return;
    saveRegieSimulation(selectedSalary._id, inputs, token, () => {
      setInputs({ ...emptyInputs, margeCible: baremes?.scalars.margeCibleDefaut ?? 0.40 });
      setResults(null);
    }, dispatch, setError);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <CssBaseline />
      <NavTab text="Matrice régie" displayReturn />
      <Grid container spacing={3} sx={{ p: 3 }}>
        <Grid item xs={12} md={7}>
          <Card sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2 }}>
              <Autocomplete
                sx={{ flexGrow: 1 }}
                options={salaries}
                getOptionLabel={(option: ISalary) => `${option.firstname} ${option.lastname}`}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
                value={selectedSalary}
                onChange={(_e, value) => setSelectedSalary(value)}
                renderInput={(params) => <TextField {...params} label="Salarié" required />}
              />
              <Button variant="outlined" startIcon={<HistoryEduIcon />} onClick={() => setHistoryOpen(true)} sx={{ flexShrink: 0 }}>
                Historique
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Salaire brut mensuel proposé (€)" value={inputs.brutMensuelPropose}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, brutMensuelPropose: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="TJM proposé (€)" value={inputs.tjmPropose}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, tjmPropose: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Marge cible (0.40 = 40%)" value={inputs.margeCible}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, margeCible: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Jours congés" value={inputs.joursConges}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, joursConges: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Jours RTT" value={inputs.joursRTT}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, joursRTT: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Jours formation" value={inputs.joursFormation}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, joursFormation: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Jours travaillés non facturés" value={inputs.joursNonFactures}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, joursNonFactures: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Absences (jours)" value={inputs.absences}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, absences: Number(e.target.value) })} />
              </Grid>

              <Grid item xs={12}><Typography variant="subtitle2">Frais de déplacement</Typography></Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Abonnement transport (€/mois)" value={inputs.abonnementTransport}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, abonnementTransport: Number(e.target.value) })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Frais annuels divers (€)" value={inputs.fraisAnnuelsDivers}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, fraisAnnuelsDivers: Number(e.target.value) })} />
              </Grid>

              <Grid item xs={4}>
                <TextField fullWidth select label="Véhicule" value={inputs.vehiculeType ?? ""}
                  onChange={(e) => setInputs({ ...inputs, vehiculeType: (e.target.value || null) as RegieVehicleType | null, puissanceFiscale: null })}>
                  <MenuItem value="">Aucun</MenuItem>
                  <MenuItem value="voiture">Voiture</MenuItem>
                  <MenuItem value="voiture_electrique">Voiture électrique</MenuItem>
                  <MenuItem value="moto">Moto</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth select label="Puissance fiscale" value={inputs.puissanceFiscale ?? ""}
                  disabled={!inputs.vehiculeType}
                  onChange={(e) => setInputs({ ...inputs, puissanceFiscale: e.target.value || null })}>
                  {puissancesFiscalesDisponibles.map((bracket) => (
                    <MenuItem key={bracket.fiscalPowerBracket} value={bracket.fiscalPowerBracket}>{bracket.fiscalPowerBracketLabel}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth type="number" label="Km prévisionnel / an" value={inputs.kmPrevisionnelAn}
                  disabled={!inputs.vehiculeType}
                  onFocus={selectAllOnFocus}
                  onChange={(e) => setInputs({ ...inputs, kmPrevisionnelAn: Number(e.target.value) })} />
              </Grid>
            </Grid>

            <Button sx={{ mt: 3 }} variant="contained" disabled={!selectedSalary || !results} onClick={handleSave}>
              Enregistrer la simulation
            </Button>
            {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <RegieResultsPanel results={results} tjmPropose={inputs.tjmPropose} />
        </Grid>
      </Grid>

      <RegieHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        salaries={salaries}
        defaultSalary={selectedSalary}
      />
    </Box>
  );
}
